import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import DropdownToggle from "./DropdownToggle";
import DropdownMenu from "./DropdownMenu";
import styles from "./dropdown.module.css";
import europe from "../../public/assets/europeUnion.svg";
import { useMyContext } from "../../myContext";
import { defaultCountry, defaultCurrency } from "../../constant";

const Dropdown = ({
  isFromStudentModal = false,
  isControlledByParent = false,
  options = [],
  label,
  heading,
  onChange,
  disabled = false,
  originalLabel = false,
  labelstyle = false,
  search = false,
  styling = "",
  Dropdownlistchange,
  showJapan = true,
  value,
  errorMessage,
}) => {
  const [selectedOption, setSelectedOption] = useState(
    isControlledByParent ? value : null
  );

  // Update selectedOption when value prop changes and component is controlled by parent
  useEffect(() => {
    if (isControlledByParent) {
      setSelectedOption(value);
    }
  }, [value, isControlledByParent]);
  const [countryOption, setCountryOption] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef(null);
  const { CountryMapping } = useMyContext();

  const filteredOption = useMemo(() => {
    if (searchText !== "") {
      return options.filter((item) =>
        item.label.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    return options;
  }, [searchText, options]);

  const handleSelectOption = useCallback(
    (option) => {
      setSelectedOption(option);
      setIsOpen(false);
      if (onChange) {
        onChange(option.value, option.label);
      }
    },
    [onChange]
  );

  const handleToggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  const capitalizeWords = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    setSelectedOption(null);
  }, [Dropdownlistchange]);

  useEffect(() => {
    setSelectedOption(null);
  }, [originalLabel]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (styling === "homepage" && CountryMapping && CountryMapping.length > 0) {
      let countryMappingOption = CountryMapping.map((item) => ({
        label:
          item.name !== defaultCountry &&
          item.name !== "UK" &&
          item.name !== "UAE"
            ? capitalizeWords(item.name)
            : item.name,
        value: item.currency || defaultCurrency,
        image: item.image,
      }));
      if (showJapan === false) {
        countryMappingOption = countryMappingOption.filter(
          (item) => item.value !== "JPY"
        );
      }
      const findDefaultCountry = countryMappingOption.find(
        (item) => item.label === defaultCountry
      );
      setCountryOption(countryMappingOption);
      if (!isControlledByParent) {
        setSelectedOption(findDefaultCountry);
      }
    } else if (
      styling === "transactionPage" &&
      CountryMapping &&
      CountryMapping.length > 0
    ) {
      const countryMappingOption = CountryMapping.filter(
        (item) => item.currency !== "EUR"
      ).map((item) => ({
        label:
          item.name !== defaultCountry &&
          item.name !== "UK" &&
          item.name !== "UAE"
            ? capitalizeWords(item.name)
            : item.name,
        value: item.currency || defaultCurrency,
        image: item.image,
      }));
      const seenCurrencies = new Set();
      const uniqueCountryMappingOption = countryMappingOption.filter((item) => {
        if (
          seenCurrencies.has(item.value) ||
          (item.value === defaultCurrency && item.label !== defaultCountry)
        ) {
          return false;
        }
        seenCurrencies.add(item.value);
        return true;
      });

      uniqueCountryMappingOption.push({
        label: "EUROPE",
        value: "EUR",
        image: europe,
      });

      const findDefaultCountry = uniqueCountryMappingOption.find(
        (item) => item.label === defaultCountry
      );

      setCountryOption(uniqueCountryMappingOption);
      if (!isControlledByParent) {
        setSelectedOption(findDefaultCountry);
      }
    }
  }, [styling, CountryMapping, showJapan, isControlledByParent]);

  useEffect(() => {
    if (styling === "quotepage") {
      const countryMappingOption = options.map((item) => ({
        label: item.label,
        value: item.value,
        image: item.image,
      }));
      const findDefaultCountry = countryMappingOption.find(
        (item) => item.label === defaultCountry
      );
      setCountryOption(countryMappingOption);
      if (!isControlledByParent) {
        setSelectedOption(findDefaultCountry);
      }
    }
  }, [styling, options, isControlledByParent]);

  const newCountryOption = useMemo(() => {
    if (selectedOption === null || countryOption.length === 0) {
      return [];
    }

    if (
      styling === "homepage" ||
      styling === "transactionPage" ||
      styling === "quotepage"
    ) {
      return countryOption.filter((item) => item !== selectedOption);
    }

    return [];
  }, [selectedOption, styling, countryOption]);

  return (
    <div ref={dropdownRef} className={styles.dropdownContainer}>
      {heading !== "" && <p className={styles.headingDropwDown}>{heading}</p>}
      <DropdownToggle
        selectedOption={selectedOption}
        label={label}
        styling={styling}
        disabled={disabled}
        labelstyle={labelstyle}
        handleToggleDropdown={handleToggleDropdown}
        isOpen={isOpen}
        errorMessage={errorMessage}
      />
      {isOpen && (
        <DropdownMenu
          isFromStudentModal={isFromStudentModal}
          options={filteredOption}
          countryOption={newCountryOption}
          styling={styling}
          handleSelectOption={handleSelectOption}
          searchText={searchText}
          setSearchText={setSearchText}
          search={search}
        />
      )}
      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}
    </div>
  );
};

export default Dropdown;
